import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Tooltip,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface Server {
  id: number;
  name: string;
  url: string;
  server_id: string;
  role: string;
  region: string | null;
  is_active: boolean;
  status: string;
  last_heartbeat: string | null;
  notes: string | null;
}

const ROLES = ['primary', 'backup', 'regional'];

export default function AdminServersPage() {
  const [servers, setServers] = useState<Server[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Server | null>(null);
  const [form, setForm] = useState({ name: '', url: '', server_id: '', role: 'primary', region: '', notes: '' });

  const load = () => api.get('/api/servers').then(r => setServers(r.data)).catch(() => {});

  useEffect(() => { load(); }, []);

  const handleOpen = (server?: Server) => {
    if (server) {
      setEditing(server);
      setForm({
        name: server.name,
        url: server.url,
        server_id: server.server_id,
        role: server.role,
        region: server.region || '',
        notes: server.notes || '',
      });
    } else {
      setEditing(null);
      setForm({ name: '', url: '', server_id: '', role: 'primary', region: '', notes: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.put(`/api/servers/${editing.id}`, { name: form.name, url: form.url, role: form.role, region: form.region || null, notes: form.notes || null });
    } else {
      await api.post('/api/servers', form);
    }
    setDialogOpen(false);
    load();
  };

  const handleDelete = async (server: Server) => {
    if (!window.confirm(`Delete server "${server.name}"?`)) return;
    await api.delete(`/api/servers/${server.id}`);
    load();
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'error';
      case 'degraded': return 'warning';
      default: return 'default';
    }
  };

  const getRoleColor = (role: string): 'primary' | 'secondary' | 'default' => {
    switch (role) {
      case 'primary': return 'primary';
      case 'backup': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Cloud Servers</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Server
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Server ID</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Region</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell>Last Heartbeat</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {servers.map(s => (
              <TableRow key={s.id}>
                <TableCell>{s.name}</TableCell>
                <TableCell><code>{s.server_id}</code></TableCell>
                <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.url}</TableCell>
                <TableCell>
                  <Chip size="small" label={s.role} color={getRoleColor(s.role)} />
                </TableCell>
                <TableCell>{s.region || '—'}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={s.status} color={getStatusColor(s.status)} />
                </TableCell>
                <TableCell>
                  {s.last_heartbeat ? new Date(s.last_heartbeat).toLocaleString() : 'Never'}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => handleOpen(s)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(s)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {servers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    No servers configured. Add your first cloud server to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Server' : 'Add Server'}</DialogTitle>
        <DialogContent>
          <TextField label="Name" fullWidth margin="normal" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="US Central Primary" />
          <TextField label="Server ID" fullWidth margin="normal" value={form.server_id} onChange={e => setForm({ ...form, server_id: e.target.value })} disabled={!!editing} placeholder="us-central-1" helperText="Unique identifier for this server instance" />
          <TextField label="URL" fullWidth margin="normal" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="wss://us-central.watchcenter365.com" />
          <TextField label="Role" fullWidth margin="normal" select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            {ROLES.map(r => <MenuItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</MenuItem>)}
          </TextField>
          <TextField label="Region" fullWidth margin="normal" value={form.region} onChange={e => setForm({ ...form, region: e.target.value })} placeholder="US Central (Dallas)" />
          <TextField label="Notes" fullWidth margin="normal" multiline rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editing ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
